/**
 * Registro estruturado.
 *
 * Duas razoes para nao usar `console.log` solto:
 *  1. "Enviar diagnostico" tem de anexar contexto real — por isso guardamos as
 *     ultimas 500 entradas em memoria e gravamo-las em `_meta` quando ha um erro.
 *  2. Ha dados que nunca podem sair do dispositivo (ADR-0006). Entidades marcadas
 *     `sensitivity: 'high'` — medicacao, documentos — sao redigidas antes de
 *     entrarem no buffer. Redigir na escrita e nao na leitura: se estivesse no
 *     buffer, um dump acidental ja tinha exposto o valor.
 */
import type { Logger } from '@flos/shared';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_RANK: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

export interface LogEntry {
  at: string;
  level: LogLevel;
  msg: string;
  /** Contexto herdado (plugin, entidade, sessao). */
  bindings: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

/** Texto que substitui qualquer valor sensivel. Em portugues, como o resto. */
export const REDACTED = '[redigido]';

/**
 * Chaves cujo valor nunca e registrado, venha de onde vier. Nao substitui a
 * verificacao por entidade: e uma segunda rede, para segredos soltos.
 */
const SECRET_KEY = /(senha|password|token|secret|segredo|api[-_]?key|chave|pin|cvv|iban)/i;

/**
 * Chaves seguras mesmo em entidades sensiveis: sao identificadores e metadados
 * estruturais, nao conteudo clinico ou financeiro. Sem elas o diagnostico ficaria
 * inutil ("aconteceu alguma coisa, algures").
 */
const STRUCTURAL_KEYS = new Set([
  'entity',
  'entidade',
  'recordId',
  'id',
  'operation',
  'operacao',
  'version',
  'versao',
  'familyId',
  'memberId',
  'syncState',
  'plugin',
  'count',
  'total',
  'durationMs',
  'attempts',
]);

export interface LoggerSink {
  (entry: LogEntry): void;
}

export interface LogPersistence {
  (entries: LogEntry[]): Promise<void> | void;
}

export interface LoggerOptions {
  level?: LogLevel;
  capacity?: number;
  sink?: LoggerSink;
}

/** Estado partilhado entre o logger raiz e todos os seus filhos. */
class LoggerCore {
  level: LogLevel;
  readonly capacity: number;
  readonly buffer: LogEntry[] = [];
  sink: LoggerSink;
  persistence: LogPersistence | null = null;
  /** Entidades com `sensitivity: 'high'`, alimentadas pelo registro de esquema. */
  readonly sensitiveEntities = new Set<string>();
  /** Ultima gravacao, para nao martelar o IndexedDB a cada erro em cascata. */
  lastPersistAt = 0;
  persistIntervalMs = 2_000;
  pending: Promise<void> = Promise.resolve();

  constructor(options: LoggerOptions) {
    this.level = options.level ?? 'info';
    this.capacity = options.capacity ?? 500;
    this.sink = options.sink ?? consoleSink;
  }

  push(entry: LogEntry): void {
    this.buffer.push(entry);
    if (this.buffer.length > this.capacity) {
      this.buffer.splice(0, this.buffer.length - this.capacity);
    }
  }
}

function consoleSink(entry: LogEntry): void {
  const prefix = `[flos] ${entry.level.toUpperCase()}`;
  const context =
    Object.keys(entry.bindings).length > 0 ? { ...entry.bindings, ...entry.meta } : entry.meta;
  const args: unknown[] = context ? [prefix, entry.msg, context] : [prefix, entry.msg];
  switch (entry.level) {
    case 'error':
      console.error(...args);
      break;
    case 'warn':
      console.warn(...args);
      break;
    case 'debug':
      console.debug(...args);
      break;
    default:
      console.info(...args);
  }
}

export class FlosLogger implements Logger {
  readonly #core: LoggerCore;
  readonly #bindings: Record<string, unknown>;

  constructor(options: LoggerOptions = {}, core?: LoggerCore, bindings: Record<string, unknown> = {}) {
    this.#core = core ?? new LoggerCore(options);
    this.#bindings = bindings;
  }

  child(bindings: Record<string, unknown>): FlosLogger {
    return new FlosLogger({}, this.#core, { ...this.#bindings, ...bindings });
  }

  debug(msg: string, meta?: Record<string, unknown>): void {
    this.#write('debug', msg, meta);
  }

  info(msg: string, meta?: Record<string, unknown>): void {
    this.#write('info', msg, meta);
  }

  warn(msg: string, meta?: Record<string, unknown>): void {
    this.#write('warn', msg, meta);
  }

  error(msg: string, meta?: Record<string, unknown>): void {
    this.#write('error', msg, meta);
  }

  // --- Configuracao (so faz sentido no raiz, mas o estado e partilhado) ---

  setLevel(level: LogLevel): void {
    this.#core.level = level;
  }

  get level(): LogLevel {
    return this.#core.level;
  }

  setSink(sink: LoggerSink): void {
    this.#core.sink = sink;
  }

  /** Liga a persistencia em `_meta`. Chamado pelo `createCore` apos abrir a BD. */
  setPersistence(persistence: LogPersistence | null): void {
    this.#core.persistence = persistence;
  }

  /** Declara as entidades sensiveis. Vem dos `EntityDescriptor` dos plugins. */
  markSensitive(entities: Iterable<string>): void {
    for (const name of entities) this.#core.sensitiveEntities.add(name);
  }

  isSensitive(entity: string): boolean {
    return this.#core.sensitiveEntities.has(entity);
  }

  /** Copia das ultimas entradas. Usado pela tela "enviar diagnostico". */
  recent(limit = 500): LogEntry[] {
    return this.#core.buffer.slice(-limit);
  }

  clearBuffer(): void {
    this.#core.buffer.length = 0;
  }

  /** Forca a gravacao do buffer. Devolve quando terminar (util em testes). */
  async flush(): Promise<void> {
    this.#persist(true);
    await this.#core.pending;
  }

  #write(level: LogLevel, msg: string, meta?: Record<string, unknown>): void {
    if (LEVEL_RANK[level] < LEVEL_RANK[this.#core.level]) return;
    const entry: LogEntry = {
      at: new Date().toISOString(),
      level,
      msg,
      bindings: this.#bindings,
    };
    const safeMeta = meta ? this.#redact(meta) : undefined;
    if (safeMeta) entry.meta = safeMeta;

    this.#core.push(entry);
    try {
      this.#core.sink(entry);
    } catch {
      // Um sink partido nao pode derrubar quem esta a registrar. Sem console aqui:
      // o console e provavelmente a origem da falha.
    }
    if (level === 'error') this.#persist(false);
  }

  /**
   * Redige o contexto. A regra e por entidade: se `meta.entity` (ou o binding
   * herdado) aponta para uma entidade de sensibilidade alta, so sobrevivem as
   * chaves estruturais. Fora disso, redigimos por nome de chave.
   */
  #redact(meta: Record<string, unknown>): Record<string, unknown> {
    const entity =
      typeof meta['entity'] === 'string'
        ? meta['entity']
        : typeof this.#bindings['entity'] === 'string'
          ? (this.#bindings['entity'] as string)
          : null;
    const wholeRecordIsSensitive = entity != null && this.#core.sensitiveEntities.has(entity);

    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(meta)) {
      if (STRUCTURAL_KEYS.has(key)) {
        out[key] = value;
        continue;
      }
      if (wholeRecordIsSensitive || SECRET_KEY.test(key)) {
        out[key] = REDACTED;
        continue;
      }
      out[key] = redactValue(value, 0);
    }
    return out;
  }

  #persist(force: boolean): void {
    const persistence = this.#core.persistence;
    if (!persistence) return;
    const now = Date.now();
    if (!force && now - this.#core.lastPersistAt < this.#core.persistIntervalMs) return;
    this.#core.lastPersistAt = now;
    const snapshot = this.#core.buffer.slice(-this.#core.capacity);
    this.#core.pending = this.#core.pending
      .then(() => persistence(snapshot))
      .then(
        () => undefined,
        (error: unknown) => {
          // Falhar a gravar o diagnostico nao pode partir o fluxo normal, mas
          // tambem nao pode ser silencioso.
          console.warn('[flos] nao foi possivel gravar o diagnostico', error);
        },
      );
  }
}

/** Redacao recursiva por nome de chave, com limite de profundidade. */
function redactValue(value: unknown, depth: number): unknown {
  if (depth > 4 || value == null) return value;
  if (Array.isArray(value)) return value.map((item) => redactValue(item, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
      out[key] = SECRET_KEY.test(key) ? REDACTED : redactValue(inner, depth + 1);
    }
    return out;
  }
  return value;
}

/** Sink silencioso — usado nos testes e no modo de producao sem consola. */
export const silentSink: LoggerSink = () => {};

export function createLogger(options: LoggerOptions = {}): FlosLogger {
  return new FlosLogger(options);
}
